import React from "react";
import { Plus, Users, Calendar, Clock, User, Phone, Mail, Car, UserCog, StickyNote, Wrench } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SectionCard } from "./AdminUI";

const Field = ({ label, icon: Icon, required, children }) => (
  <div>
    <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon size={11} />}
      {label}{required && <span className="text-[#FF3B30]">*</span>}
    </label>
    {children}
  </div>
);

const CreateAppointmentTab = ({ ctx }) => {
  const {
    manualForm, setManualForm, handleCreateManualAppointment, clients, services,
    technicians, timeSlots, durationOptions,
  } = ctx;

  return (
    <div className="space-y-5">
      <SectionCard title="Créer un rendez-vous manuel" icon={Plus} iconColor="text-emerald-400">
        <p className="text-white/50 text-sm mb-5">
          Créez un RDV pour un client qui réserve par téléphone ou directement sur place.
        </p>

        <form onSubmit={handleCreateManualAppointment} className="space-y-4">
          {/* Client selector */}
          <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
            <Field label="Sélectionner un client existant (optionnel)" icon={Users}>
              <Input
                type="text"
                list="clients-list"
                placeholder="Rechercher par nom, email ou plaque..."
                className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm"
                onChange={(e) => {
                  const value = e.target.value;
                  const match = clients.find(
                    (c) => c.client_name === value || c.client_email === value || c.plates?.includes(value)
                  );
                  if (match) {
                    setManualForm({
                      ...manualForm,
                      client_name: match.client_name || "",
                      client_email: match.client_email || "",
                      client_phone: match.client_phone || "",
                      client_plate: match.plates?.[0] || "",
                    });
                  }
                }}
              />
              <datalist id="clients-list">
                {clients.map((c) => (
                  <option key={c.client_email} value={c.client_name}>
                    {c.client_email} - {c.plates?.join(", ")}
                  </option>
                ))}
              </datalist>
            </Field>
          </div>

          {/* Service + Date + Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Service" icon={Wrench} required>
              <Input
                type="text" list="services-list"
                value={manualForm.service_name}
                onChange={(e) => {
                  const value = e.target.value;
                  const service = services.find((s) => s.name === value);
                  const isCalc = value.toLowerCase().includes("calculateur");
                  setManualForm({
                    ...manualForm,
                    service_id: service?.id || "custom",
                    service_name: value,
                    technician: isCalc ? "Abder" : manualForm.technician,
                  });
                }}
                placeholder="Sélectionner ou saisir"
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
                required
              />
              <datalist id="services-list">
                {services.map((s) => (<option key={s.id} value={s.name} />))}
              </datalist>
            </Field>
            <Field label="Date" icon={Calendar} required>
              <Input
                type="date" value={manualForm.date}
                onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
                required
              />
            </Field>
            <Field label="Heure" icon={Clock} required>
              <Select value={manualForm.time} onValueChange={(v) => setManualForm({ ...manualForm, time: v })}>
                <SelectTrigger className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm">
                  <SelectValue placeholder="Choisir" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-white/10">
                  {timeSlots.map((slot) => (<SelectItem key={slot} value={slot}>{slot}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Client name + Phone + Plate */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Nom client" icon={User} required>
              <Input
                type="text" value={manualForm.client_name}
                onChange={(e) => setManualForm({ ...manualForm, client_name: e.target.value })}
                placeholder="Nom du client"
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
                required
              />
            </Field>
            <Field label="Téléphone" icon={Phone} required>
              <Input
                type="tel" value={manualForm.client_phone}
                onChange={(e) => setManualForm({ ...manualForm, client_phone: e.target.value })}
                placeholder="06 XX XX XX XX"
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
                required
              />
            </Field>
            <Field label="Plaque" icon={Car}>
              <Input
                type="text" value={manualForm.client_plate}
                onChange={(e) => setManualForm({ ...manualForm, client_plate: e.target.value.toUpperCase() })}
                placeholder="AA-123-BB"
                className="bg-[#1C1C1E] border-white/[0.06] text-[#FF3B30] font-bold h-9 text-sm"
              />
            </Field>
          </div>

          {/* Email + Duration + Technician */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Email (optionnel)" icon={Mail}>
              <Input
                type="email" value={manualForm.client_email}
                onChange={(e) => setManualForm({ ...manualForm, client_email: e.target.value })}
                placeholder="email@exemple.com"
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
              />
            </Field>
            <Field label="Durée (min)" icon={Clock}>
              <Select
                value={manualForm.duration.toString()}
                onValueChange={(v) => setManualForm({ ...manualForm, duration: parseInt(v) })}
              >
                <SelectTrigger className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm">
                  <SelectValue placeholder="Durée" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-white/10">
                  {durationOptions.map((d) => (<SelectItem key={d} value={d.toString()}>{d} min</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Technicien" icon={UserCog}>
              <Select
                value={manualForm.technician}
                onValueChange={(v) => setManualForm({ ...manualForm, technician: v })}
              >
                <SelectTrigger className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm">
                  <SelectValue placeholder="Assigner" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-white/10">
                  {technicians.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Notes + Submit */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
            <Field label="Notes" icon={StickyNote}>
              <Input
                type="text" value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                placeholder="Notes internes..."
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
              />
            </Field>
            <button
              type="submit"
              data-testid="create-manual-apt-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5 py-2 rounded-md text-sm transition-colors flex items-center justify-center gap-2 h-9"
            >
              <Plus size={15} /> Créer le rendez-vous
            </button>
          </div>
        </form>
      </SectionCard>
    </div>
  );
};

export default CreateAppointmentTab;
